import { useMemo, useState } from 'react';

import { DateRange } from 'react-day-picker';
import { useNavigate, useParams } from 'react-router-dom';

import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { permissionsUtil } from 'utils/permissionsUtil';
import { getWholeRange } from 'views/Analytics/utils';

import DatePicker from 'components/DatePicker/DatePicker';
import { DatePickerMode } from 'components/DatePicker/DatePicker.types';

import usePlans, { DEFAULT_PLANS_FILTERS } from '../../../api/usePlans';
import DataTable from '../../../components/DataTableComponent/DataTable';
import FilterSearchCard from '../../../components/Filter/FilterSearchCard';
import { usePlansColumns } from '../hooks/use-plans-columns';
import { DEFAULT_DATE_RANGE } from '../utils';

export const PlansTable: React.FC = () => {
  const navigate = useNavigate();
  const { publisherId } = useParams();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [displayName, setDisplayName] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(0);

  const apiDateRange = useMemo(() => getWholeRange(dateRange), [dateRange]);

  const filters = useMemo(
    () => ({
      pageSize: DEFAULT_PLANS_FILTERS.pageSize,
      offset: currentPage * DEFAULT_PLANS_FILTERS.pageSize,
      startDate: apiDateRange?.from?.toISOString(),
      endDate: apiDateRange?.to?.toISOString(),
      displayName: displayName
    }),
    [currentPage, apiDateRange, displayName]
  );

  const { getPlans, archivePlan } = usePlans(filters);
  const { columns } = usePlansColumns(archivePlan, getPlans);

  const handleDateRangeChange = (value: DateRange) => {
    setDateRange(value);
    // setPreviousDateRange(undefined);
  };

  const isLoading = getPlans.isLoading;
  const isFetched = getPlans.isSuccess;
  const plans = getPlans.data?.plans || [];
  const isEmpty = plans.length === 0;
  const totalPages = Math.ceil(
    (getPlans.data?.totalCount || 0) / DEFAULT_PLANS_FILTERS.pageSize
  );

  return (
    <div>
      {isFetched && (
        <div className="flex pb-3 pt-1 gap-3">
          <FilterSearchCard
            header="Display name"
            filterValue={displayName}
            setFilterValue={(value) => setDisplayName(value)}
          />

          <div>
            <DatePicker
              shouldDisplayTime={false}
              mode={DatePickerMode.RANGE}
              value={dateRange}
              onApply={(value) => handleDateRangeChange(value as DateRange)}
              shouldExcludeFuture
            />
          </div>
        </div>
      )}

      <DataTable
        data={plans}
        columns={columns}
        rowIdIdentifier="_id"
        bodyDataTestId="dashPlansList"
        isLoading={isLoading}
        rowCount={getPlans.data?.totalCount}
        rowsPerPage={DEFAULT_PLANS_FILTERS.pageSize}
        showPagination={totalPages > 1}
        manualPagination
        pageIndex={currentPage}
        pageCount={totalPages}
        onPageChange={setCurrentPage}
        emptyStateConfig={{
          title: ' plan',
          isEmpty,
          description: (
            <>
              A subscription plan enables recurring access for your players and
              can be
              <br />
              configured to suit your monetization strategy.
            </>
          ),
          disableButton: !permissionsUtil.isActionEnabled(
            null,
            EPermissionGroup.SUBSCRIPTIONS,
            EPermissionAction.MANAGE,
            publisherId
          ),
          onClick: () =>
            permissionsUtil.isActionEnabled(
              null,
              EPermissionGroup.SUBSCRIPTIONS,
              EPermissionAction.MANAGE,
              publisherId
            ) && navigate('./form')
        }}
        bordered
      />
    </div>
  );
};
